import React from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@material-ui/core';
import { DataTableProps } from './types';
import { useSortableData } from './useSortableData';
import { useColumnVisibility } from './useColumnVisibility';
import { useFEPagination } from './useFEPagination';
import { DataTableHeader } from './DataTableHeader';
import { DataTableBody } from './DataTableBody';
import { useCommonStyles } from '../../../styles';
import { DataTableFooter } from './DataTableFooter';

export function DataTable<T extends Record<string, any>>({
  columns,
  data,
  emptyMessage,
  translation,
  columnSelect,
  selectedColumns: controlledSelectedColumns,
  onSelectColumns,
  sortable = true,
  defaultSort,
  pagination = 'showMore',
  onPageChange,
  rowsPerPage = 40,
  onRowAction,
  rowActionIcon,
  groupByKey,
  className,
  headerClassName,
}: DataTableProps<T>) {
  const commonClasses = useCommonStyles();

  const { selectedColumns, handleSelectColumns, visibleColumns, allColumnLabels } = useColumnVisibility(columns, controlledSelectedColumns, onSelectColumns);

  const { sortedData, requestSort, getSortDirection, isColumnSorted } = useSortableData(data, visibleColumns, defaultSort);

  const { displayedRows, handleShowMore, hasMore, visibleCount, totalCount } = useFEPagination(sortedData, rowsPerPage);

  const isShowMore = pagination === 'showMore';
  const rows = isShowMore ? displayedRows : sortedData;
  const columnsCount = visibleColumns.length + (columnSelect || onRowAction ? 1 : 0);

  return (
    <>
      <TableContainer className={className}>
        <Table size="small">
          <TableHead>
            <DataTableHeader
              columns={visibleColumns}
              sortable={sortable}
              onSort={requestSort}
              getSortDirection={getSortDirection}
              isColumnSorted={isColumnSorted}
              columnSelect={columnSelect}
              translation={translation}
              allColumnLabels={allColumnLabels}
              selectedColumns={selectedColumns}
              onSelectColumns={handleSelectColumns}
              headerClassName={headerClassName}
              groupByKey={groupByKey}
            />
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow className={commonClasses.tableRow}>
                <TableCell align="center" colSpan={columnsCount}>
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              <DataTableBody data={rows} columns={visibleColumns} groupByKey={groupByKey} onRowAction={onRowAction} rowActionIcon={rowActionIcon} columnSelect={columnSelect} />
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <DataTableFooter
        pagination={pagination}
        translation={translation}
        hasMore={hasMore}
        visibleCount={visibleCount}
        totalCount={totalCount}
        onShowMore={handleShowMore}
        rowsPerPage={rowsPerPage}
        rowCount={sortedData.length}
        onPageChange={onPageChange}
      />
    </>
  );
}
